// Core packages
import {
  Component, Inject,
  OnDestroy, OnInit
} from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Sort } from "@angular/material/sort";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import moment from "moment";
import { CookieService } from "ngx-cookie-service";
import { Subscription } from "rxjs";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { IUser } from "src/app/shared/models/user/user.interface";
import { User } from "src/app/shared/models/user/user.model";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { PlatformNotesService } from "../../platformNotes.service";

@Component({
  selector: "app-view-platform-notes-dialog",
  templateUrl: "./view-platform-notes-dialog.component.html",
  styleUrls: ["./view-platform-notes-dialog.component.scss"]
})
export class ViewPlatformNotesDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  user: User = new User();
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - Add subUser form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly

  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.add"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true
    }
  };
  // END - Add subUser form

  dialogIsLoading = true;

  propertyId = '';
  companyId = '';
  propertyIdBalances = '';
  companyIdBalances = '';
  notesBalance: any[]= [];
  notesProperty: any[]= [];
  notesCompany: any[]= [];
  sortedDataBalance: any[]= [];
  sortedDataProperty: any[]= [];
  sortedDataCompany: any[]= [];

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<ViewPlatformNotesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public configService: ConfigService,
    private cookieService: CookieService,
    private translateService: TranslateService,
    private authService: AuthService,
    private platformNotesService: PlatformNotesService,
    private apiService: ApiService
  ) {
    dialogRef.disableClose = true;
  }

  /**
   * Init component
   *
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => (this.loggedUser = res))
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // View data
    if (this.data && this.data.propertyId) {
      this.propertyId = this.data.propertyId;
      this.subscriptions.push(
        this.platformNotesService.getProperty(this.propertyId, 0, 100)
        .subscribe((res) => {
          if (res.data.items && Array.isArray(res.data.items)) {
            for (const item of res.data.items) {
              const createdBy = `${item.createdBy.firstName} ${item.createdBy.lastName}`;
              let createdAt = '';
              if (
                item.createdAt && 
                moment(item.createdAt).isValid()
              ) {
                createdAt = moment(item.createdAt).format("DD/MM/YYYY hh:mm");
              }
              let category = '';
              switch (item.category) {
                case "subscription": 
                  category = this.translateService.instant("platformNotes.categories.subscription"); 
                break;
                case "work-account":
                  category = this.translateService.instant("platformNotes.categories.work-account");
                  break;
                case "technical-operational":
                  category = this.translateService.instant("platformNotes.categories.technical-operational");
                  break;
                case "insertion":
                  category = this.translateService.instant("platformNotes.categories.insertion");
                  break;
                case "mapping":
                  category = this.translateService.instant("platformNotes.categories.mapping");
                  break;
                case "packaging":
                  category = this.translateService.instant("platformNotes.categories.packaging");
                  break;
                case "tagnfc":
                  category = this.translateService.instant("platformNotes.categories.tagnfc");
                  break;
                case "suppliers":
                  category = this.translateService.instant("platformNotes.categories.suppliers");
                  break;
                case "other":
                  category = this.translateService.instant("platformNotes.categories.other");
                  break;
              }
              if (item.createdIn === 'company'){
                this.notesCompany.push({
                  category,
                  createdBy,
                  createdAt,
                  title: item.title,
                  description: item.description,
                  originalName: item.file && item.file.originalName ? item.file.originalName : '',
                  file: item.file
                });
              } else {
                const property = item.properties.map((el: any) => el.name).join();
                this.notesProperty.push({
                  property,
                  category,
                  createdBy,
                  createdAt,
                  title: item.title,
                  description: item.description,
                  originalName: item.file && item.file.originalName ? item.file.originalName : '',
                  file: item.file
                });
              }
              
            }
            this.sortedDataCompany = this.notesCompany.slice();
            this.sortedDataProperty = this.notesProperty.slice();
            this.sortDataCompany({ active: "createdAt", direction: "desc" });
            this.sortDataProperty({ active: "createdAt", direction: "desc" });
          }
          // Stop loading dialog
          this.dialogIsLoading = false;
        })
      );
    } else if (this.data && this.data.companyId) {
      this.companyId = this.data.companyId;
      this.subscriptions.push(
        this.platformNotesService.getCompanyWithProperties(this.companyId, 0, 100)
        .subscribe((res) => {
          if (res.data.items && Array.isArray(res.data.items)) {
            for (const item of res.data.items) {
              const createdBy = `${item.createdBy.firstName} ${item.createdBy.lastName}`;
              let createdAt = '';
              if (
                item.createdAt && 
                moment(item.createdAt).isValid()
              ) {
                createdAt = moment(item.createdAt).format("DD/MM/YYYY hh:mm");
              }
              let category = '';
              switch (item.category) {
                case "subscription": 
                  category = this.translateService.instant("platformNotes.categories.subscription"); 
                break;
                case "work-account":
                  category = this.translateService.instant("platformNotes.categories.work-account");
                  break;
                case "technical-operational":
                  category = this.translateService.instant("platformNotes.categories.technical-operational");
                  break;
                case "insertion":
                  category = this.translateService.instant("platformNotes.categories.insertion");
                  break;
                case "mapping":
                  category = this.translateService.instant("platformNotes.categories.mapping");
                  break;
                case "packaging":
                  category = this.translateService.instant("platformNotes.categories.packaging");
                  break;
                case "tagnfc":
                  category = this.translateService.instant("platformNotes.categories.tagnfc");
                  break;
                case "suppliers":
                  category = this.translateService.instant("platformNotes.categories.suppliers");
                  break;
                case "other":
                  category = this.translateService.instant("platformNotes.categories.other");
                  break;
              }
              if (item.createdIn === 'company'){
                this.notesCompany.push({
                  category,
                  createdBy,
                  createdAt,
                  title: item.title,
                  description: item.description,
                  originalName: item.file && item.file.originalName ? item.file.originalName : '',
                  file: item.file
                });
              } else {
                const property = item.properties.map((el: any) => el.name).join();
                this.notesProperty.push({
                  property,
                  category,
                  createdBy,
                  createdAt,
                  title: item.title,
                  description: item.description,
                  originalName: item.file && item.file.originalName ? item.file.originalName : '',
                  file: item.file
                });
              }
              
            }
            this.sortedDataCompany = this.notesCompany.slice();
            this.sortedDataProperty = this.notesProperty.slice();
            this.sortDataCompany({ active: "createdAt", direction: "desc" });
            this.sortDataProperty({ active: "createdAt", direction: "desc" });
          }
          // Stop loading dialog
          this.dialogIsLoading = false;
        })
      );
    } else if (this.data && this.data.propertyIdBalances) {
      this.propertyIdBalances = this.data.propertyIdBalances;
      this.subscriptions.push(
        this.platformNotesService.getPropertyBalancesNotes(this.propertyIdBalances)
        .subscribe((res) => {
          if (res.data.items && Array.isArray(res.data.items)) {
            for (const item of res.data.items) {
              const createdBy = `${item.createdBy.firstName} ${item.createdBy.lastName}`;
              let createdAt = '';
              if (
                item.createdAt && 
                moment(item.createdAt).isValid()
              ) {
                createdAt = moment(item.createdAt).format("DD/MM/YYYY hh:mm");
              }
              let category = '';
              switch (item.category) {
                case "subscription": 
                  category = this.translateService.instant("platformNotes.categories.subscription"); 
                break;
                case "work-account":
                  category = this.translateService.instant("platformNotes.categories.work-account");
                  break;
                case "technical-operational":
                  category = this.translateService.instant("platformNotes.categories.technical-operational");
                  break;
                case "insertion":
                  category = this.translateService.instant("platformNotes.categories.insertion");
                  break;
                case "mapping":
                  category = this.translateService.instant("platformNotes.categories.mapping");
                  break;
                case "packaging":
                  category = this.translateService.instant("platformNotes.categories.packaging");
                  break;
                case "tagnfc":
                  category = this.translateService.instant("platformNotes.categories.tagnfc");
                  break;
                case "suppliers":
                  category = this.translateService.instant("platformNotes.categories.suppliers");
                  break;
                case "other":
                  category = this.translateService.instant("platformNotes.categories.other");
                  break;
              }
              this.notesBalance.push({
                balance: item.balance,
                category,
                createdBy,
                createdAt,
                title: item.title,
                description: item.text,
                originalName: item.file && item.file.originalName ? item.file.originalName : '',
                file: item.file
              });
              
            }
            this.sortedDataBalance = this.notesBalance.slice();
            this.sortDataBalance({ active: "createdAt", direction: "desc" });
          }
          // Stop loading dialog
          this.dialogIsLoading = false;
        })
      );
    } else if (this.data && this.data.companyIdBalances) {
      this.companyIdBalances = this.data.companyIdBalances;
      this.subscriptions.push(
        this.platformNotesService.getCompanyBalancesNotes(this.companyIdBalances)
        .subscribe((res) => {
          if (res.data.items && Array.isArray(res.data.items)) {
            for (const item of res.data.items) {
              const createdBy = `${item.createdBy.firstName} ${item.createdBy.lastName}`;
              let createdAt = '';
              if (
                item.createdAt && 
                moment(item.createdAt).isValid()
              ) {
                createdAt = moment(item.createdAt).format("DD/MM/YYYY hh:mm");
              }
              let category = '';
              switch (item.category) {
                case "subscription": 
                  category = this.translateService.instant("platformNotes.categories.subscription"); 
                break;
                case "work-account":
                  category = this.translateService.instant("platformNotes.categories.work-account");
                  break;
                case "technical-operational":
                  category = this.translateService.instant("platformNotes.categories.technical-operational");
                  break;
                case "insertion":
                  category = this.translateService.instant("platformNotes.categories.insertion");
                  break;
                case "mapping":
                  category = this.translateService.instant("platformNotes.categories.mapping");
                  break;
                case "packaging":
                  category = this.translateService.instant("platformNotes.categories.packaging");
                  break;
                case "tagnfc":
                  category = this.translateService.instant("platformNotes.categories.tagnfc");
                  break;
                case "suppliers":
                  category = this.translateService.instant("platformNotes.categories.suppliers");
                  break;
                case "other":
                  category = this.translateService.instant("platformNotes.categories.other");
                  break;
              }
              this.notesBalance.push({
                balance: item.balance,
                category,
                createdBy,
                createdAt,
                title: item.title,
                description: item.text,
                originalName: item.file && item.file.originalName ? item.file.originalName : '',
                file: item.file
              });
              
            }
            this.sortedDataBalance = this.notesBalance.slice();
            this.sortDataBalance({ active: "createdAt", direction: "desc" });
          }
          // Stop loading dialog
          this.dialogIsLoading = false;
        })
      );
    } else {
      // Stop loading dialog
      this.dialogIsLoading = false;
    }
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   */
  onClose(): void {
    this.dialogRef.close();
  }

  /**
   * Prints the whole HTML contained in "dataToPrint" element
   *
   * @since 1.0.0
   */
  onPrint(): void {
    let title = this.translateService.instant("platformNotes.dialog.propertyNotes.title");
    title = title.replace(" ","-");
    const printData = document.getElementById('dataToPrint').innerHTML;
    const openWindow = window.open(
      "", 
      title, 
      ""
    );
    openWindow.document.write('<style type="text/css">');
    openWindow.document.write('  @media print {');
    openWindow.document.write('    .clickable .mat-sort-header-container {');
    openWindow.document.write('      text-align: start !important;');
    openWindow.document.write('    }');
    openWindow.document.write('    .clickable .mat-sort-header-container .mat-sort-header-button{');
    openWindow.document.write('      border-width: 0px !important;');
    openWindow.document.write('      background-color: #FFF;');
    openWindow.document.write('    }');
    openWindow.document.write('  }');
    openWindow.document.write('</style>');

    openWindow.document.write(printData);
    openWindow.document.close();
    openWindow.focus();
    openWindow.print();
    openWindow.close();
  }
  
  /**
   * Handle table sort
   *
   * @since 1.0.0
   */
  sortDataCompany(sort: Sort): void {
    const data = this.sortedDataCompany.slice();
    if (!sort.active || sort.direction === "") {
      this.sortedDataCompany = data;
      return;
    }

    this.sortedDataCompany = data.sort((a, b) => {
      const isAsc = sort.direction === "asc";
      switch (sort.active) {
        case "category":
          return this.compare(a.category, b.category, isAsc);
        case "title":
          return this.compare(a.title, b.title, isAsc);
        case "description":
          return this.compare(a.description, b.description, isAsc);
        case "createdAt":
          return this.compare(a.createdAt, b.createdAt, isAsc);
        case "createdBy":
          return this.compare(a.createdBy, b.createdBy, isAsc);
        case "originalName":
          return this.compare(a.originalName, b.originalName, isAsc);
        default:
          return 0;
      }
    });
  }
  sortDataProperty(sort: Sort): void {
    const data = this.sortedDataProperty.slice();
    if (!sort.active || sort.direction === "") {
      this.sortedDataProperty = data;
      return;
    }

    this.sortedDataProperty = data.sort((a, b) => {
      const isAsc = sort.direction === "asc";
      switch (sort.active) {
        case "property": 
          return this.compare(a.property, b.property, isAsc);
        case "category":
          return this.compare(a.category, b.category, isAsc);
        case "title":
          return this.compare(a.title, b.title, isAsc);
        case "description":
          return this.compare(a.description, b.description, isAsc);
        case "createdAt":
          return this.compare(a.createdAt, b.createdAt, isAsc);
        case "createdBy":
          return this.compare(a.createdBy, b.createdBy, isAsc);
        case "originalName":
          return this.compare(a.originalName, b.originalName, isAsc);
        default:
          return 0;
      }
    });
  }
  sortDataBalance(sort: Sort): void {
    const data = this.sortedDataBalance.slice();
    if (!sort.active || sort.direction === "") {
      this.sortedDataBalance = data;
      return;
    }

    this.sortedDataBalance = data.sort((a, b) => {
      const isAsc = sort.direction === "asc";
      switch (sort.active) {
        case "category":
          return this.compare(a.category, b.category, isAsc);
        case "title":
          return this.compare(a.title, b.title, isAsc);
        case "description":
          return this.compare(a.description, b.description, isAsc);
        case "createdAt":
          return this.compare(a.createdAt, b.createdAt, isAsc);
        case "createdBy":
          return this.compare(a.createdBy, b.createdBy, isAsc);
        case "originalName":
          return this.compare(a.originalName, b.originalName, isAsc);
        default:
          return 0;
      }
    });
  }

  /**
   * Utility per table sort
   *
   * @since 1.0.0
   */
  compare(a: number | string, b: number | string, isAsc: boolean): any {
    if (typeof a === 'string') {
      a = a.toLowerCase();
    }
    if (typeof b === 'string') {
      b = b.toLowerCase();
    }
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }
}
